import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import Tooltip, { DefaultTooltipPopperProps } from 'js/components/elements/mui/Tooltip';
import {
  CONNECTION_CATEGORY_DATABASE,
  CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI,
} from 'js/constants/connectionCategories';
import { RESOURCE_TYPE_SINK, RESOURCE_TYPE_SOURCE } from 'js/constants/resourceTypes';
import { each, filter, get, has, isArray, isEmpty, isPlainObject, keys, map } from 'lodash';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { CapabilityChecker } from 'new/components/flows/configure/ELT/ELTSourceSetup/constants/capabilities';
import { processV2Template } from 'new/components/flows/configure/ELT/ELTSourceSetup/utils/v2TemplateProcessor';
import flowTypes from 'new/components/flows/types/flowTypes';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import useResourceEditProvider from 'js/components/ResourceEditProvider/useResourceEditProvider';
import { ResourceEditContext } from 'js/components/ResourceEditProvider/ResourceEditContext';
import SinkColumnMapping from '../../../constants/sinkColumnMapping';
import UIUtils from '../../../lib/UIUtils';
import ConfigFieldValue from '../../data_sources/cards/configViews/ConfigFieldValue';
import {
  getConfigContentForCDC,
  getCDCTablesListContent,
  isSqlQueryField,
} from './ConfigurationFields.utils';
import FeedsListWithSearch from './FeedsListWithSearch';
import { Button } from 'new/elements/Button';

// Custom conditional display logic for ELT flows (extracted from ResourceParamFields)
const shouldRenderFieldForELT = (field, values) => {
  const { conditionalDisplay, displayOn, inputType } = field;

  // First check the standard shouldRenderField logic
  let shouldRender = UIUtils.shouldRenderField(field, values);

  // For ELT flows, also apply conditional display logic to non-button input types
  if (conditionalDisplay === true && shouldRender === true && inputType !== 'button') {
    each(displayOn, (critField, idx) => {
      if (has(values, critField.name) && values[critField.name] !== critField.value) {
        shouldRender = false;
        return false;
      }
    });
  }
  return shouldRender;
};

// Template version detection utility
const detectTemplateVersion = (data, vendors, connectionCategory) => {
  // First, try to get vendor data from the vendors prop
  if (vendors && data?.vendor_id) {
    const vendor = vendors[data.vendor_id];
    if (vendor) {
      // Check vendor endpoints for v2 template type
      if (vendor.vendor_endpoints) {
        const hasV2Endpoint = vendor.vendor_endpoints.some(
          (ep) => ep.source_template?.template_type === 'v2',
        );

        if (hasV2Endpoint) {
          return 'v2';
        }
      }

      // Check vendor config for v2 template type
      if (vendor.config?.template_type === 'v2') {
        return 'v2';
      }
    }
  }

  // Fallback to checking source data structure
  // Check if it's explicitly marked as v2 in the data
  if (data?.template_config?.template_type === 'v2') {
    return 'v2';
  }

  // Check if it's explicitly marked as v1
  if (data?.template_config?.template_type === 'v1') {
    return 'v1';
  }

  // Check if any vendor_endpoints have v2 template_type
  if (data?.vendor_endpoint?.vendor_spec?.vendor_endpoints) {
    const hasV2Endpoint = data.vendor_endpoint.vendor_spec.vendor_endpoints.some(
      (ep) => ep.source_template?.template_type === 'v2',
    );

    if (hasV2Endpoint) {
      return 'v2';
    }
  }

  // Check if the vendor spec has v2 template type
  if (data?.vendor_endpoint?.vendor_spec?.config?.template_type === 'v2') {
    return 'v2';
  }

  // Check if the data has v2-specific structures (strategy mappings, etc.)
  if (data?.vendor_endpoint?.vendor_spec?.strategyMappings) {
    return 'v2';
  }

  // Check if ref_feed_options has strategies (v2) vs display (v1)
  if (data?.vendor_endpoint?.vendor_spec?.ref_feed_options?.strategies) {
    return 'v2';
  }

  // Default to v1 (legacy)
  return 'v1';
};

const attributeColumnMappingTitle = 'Attribute -> Column Mapping';

import './ConfigurationFields.scss';
import './StyledDetailsAccordionGroup.scss';

// Custom hook for field filtering
const useFieldFiltering = (fields, entityType, connectionCategory, config, fieldsToIgnore) => {
  return useMemo(() => {
    return filter(fields, (field) =>
      entityType === RESOURCE_TYPE_SINK
        ? !UIUtils.isFieldInSinkLocation(field.name, connectionCategory) &&
          fieldsToIgnore.indexOf(field.name) < 0 &&
          has(config, field.name) &&
          config[field.name] !== ''
        : !UIUtils.isFieldInSourceLocation(field.name, connectionCategory) &&
          fieldsToIgnore.indexOf(field.name) < 0 &&
          has(config, field.name) &&
          config[field.name] !== '',
    );
  }, [fields, entityType, connectionCategory, config, fieldsToIgnore]);
};

// Custom hook for accordion state management
const useAccordionState = (initialExpanded = true) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const handleAccordionToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return { isExpanded, handleAccordionToggle };
};

// Custom hook for mapping content
const useMappingContent = (
  config,
  entityType,
  connectionCategory,
  flowType,
  isExpanded,
  handleAccordionToggle,
) => {
  return useMemo(() => {
    if (!has(config, 'mapping')) return null;

    const configMapping = config.mapping;
    const [col1, col2] =
      entityType === RESOURCE_TYPE_SINK
        ? get(
            SinkColumnMapping,
            `${entityType}.${connectionCategory}`,
            SinkColumnMapping[entityType].default,
          )
        : get(SinkColumnMapping, 'default');

    let mappingContent;
    if (has(configMapping, 'mode') && configMapping.mode === 'manual') {
      let attrsMapping = configMapping.mapping;
      mappingContent = (
        <div className="details-accordion-group" aria-label="details-accordion-group-container">
          <Accordion
            className="accordion-item"
            key="mapping-accordion"
            expanded={isExpanded}
            onChange={(event, expanded) => handleAccordionToggle()}
          >
            <AccordionSummary
              className="accordion-title"
              expandIcon={<ExpandMore fontSize="large" />}
            >
              <div className="accordion-title-content">
                <span className="title">{attributeColumnMappingTitle}</span>
                <div className="toggle-text" />
              </div>
            </AccordionSummary>

            <AccordionDetails className="accordion-body">
              <div className="accordion-body-content">
                <div className="full-width-table">
                  <div className="table-header">
                    <Grid container className="table-header-row">
                      <Grid item xs={6}>
                        {col1}
                      </Grid>
                      <Grid item xs={6}>
                        {col2}
                      </Grid>
                    </Grid>
                  </div>
                  <div className={'table-body'}>
                    {map(attrsMapping, (col, k) => {
                      if (isPlainObject(col)) {
                        return map(keys(col), (colName, l) => (
                          <Grid container key={`mapping-${k}-${l}`} className={'table-body-row'}>
                            <Grid item xs={6}>
                              {k}
                            </Grid>
                            <Grid item xs={6}>
                              {colName}
                            </Grid>
                          </Grid>
                        ));
                      } else {
                        return (
                          <Grid container key={`mapping-${k}`} className={'table-body-row'}>
                            <Grid item xs={6}>
                              {k}
                            </Grid>
                            <Grid item xs={6}>
                              {col}
                            </Grid>
                          </Grid>
                        );
                      }
                    })}
                  </div>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      );
    } else {
      mappingContent = (
        <>
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-key">Attribute - Column Mapping</div>
              <div className="detail-value">Automatic</div>
            </div>
            {isArray(configMapping.excludes) && !isEmpty(configMapping.excludes) && (
              <div className="detail-col">
                <div className="detail-key">Excluded Attributes</div>
                <div className="detail-value">{configMapping.excludes.join(', ')}</div>
              </div>
            )}
          </div>
          <div className="detail-row">
            {flowType === flowTypes.elt && (
              <div className="detail-col">
                <div className="detail-key">Table Name Prefix</div>
                <div className="detail-value">{configMapping?.table_name_prefix || ''}</div>
              </div>
            )}
            {flowType === flowTypes.elt && (
              <div className="detail-col">
                <div className="detail-key">Table Name Suffix</div>
                <div className="detail-value">{configMapping?.table_name_suffix || ''}</div>
              </div>
            )}
          </div>
        </>
      );
    }
    return mappingContent;
  }, [config, entityType, connectionCategory, flowType, isExpanded, handleAccordionToggle]);
};

// Custom hook for ELT config content
const useELTConfigContent = (
  flowType,
  connectionCategory,
  entityType,
  data,
  config,
  isExpanded,
  handleAccordionToggle,
  configFields,
  isLoadingResource,
  vendors,
  sourceConfigById,
  vendorEndpoints,
  vendorEndpointParams,
) => {
  return useMemo(() => {
    if (
      !(
        flowType === flowTypes.elt ||
        (flowType === flowTypes.streaming &&
          connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI)
      )
    ) {
      return null;
    }

    if (entityType === RESOURCE_TYPE_SOURCE) {
      const templateVersion = detectTemplateVersion(data, vendors, connectionCategory);
      const sourceFeeds = data?.template_config?.feeds_config?.feeds || {};

      const feedsList = Object.keys(sourceFeeds).map((feedName) => {
        const feed = sourceFeeds[feedName];

        // Handle both v1 and v2 template structures
        let displayName = feedName;
        if (templateVersion === 'v2') {
          // V2 templates use different structure for display names
          displayName =
            feed?.spec?.params?.['ui.feed_display_name'] ||
            feed?.spec?.params?.['feed_spec.display_name'] ||
            feedName;
        } else {
          // V1 templates use the traditional structure
          displayName = feed?.spec?.params?.['ui.feed_display_name'] || feedName;
        }

        return {
          label: displayName,
          value: feedName,
          feedProps: feed,
          templateVersion,
        };
      });

      const feeds = { list: feedsList, feedSpecs: sourceFeeds };

      // Get user-configurable fields dynamically from sourceConfig
      const userConfigFields = [];

      // Get current values from the data params
      const currentParams = data?.template_config?.params || {};

      // Add vendor_endpoint_id to currentParams for dependency checks
      // Handle different ways vendor_endpoint might be stored
      let vendorEndpointId = null;

      // Check if it's directly in params
      if (currentParams.vendor_endpoint_id !== undefined) {
        vendorEndpointId = currentParams.vendor_endpoint_id;
      }
      // Check if it's in data.vendor_endpoint.id
      else if (data?.vendor_endpoint?.id !== undefined) {
        vendorEndpointId = data.vendor_endpoint.id;
      }
      // Check if it's in data.vendor_endpoint (direct ID)
      else if (data?.vendor_endpoint !== undefined && typeof data.vendor_endpoint === 'number') {
        vendorEndpointId = data.vendor_endpoint;
      }

      // Ensure it's a number and add to currentParams
      if (vendorEndpointId !== null && !isNaN(Number(vendorEndpointId))) {
        currentParams.vendor_endpoint_id = Number(vendorEndpointId);
      } else {
        // If no valid vendor_endpoint_id found, add undefined to ensure the field exists
        // This allows dependency checks to run (even if they fail)
        currentParams.vendor_endpoint_id = undefined;
      }

      // Apply the same logic as the edit form: add missing defaults first
      let processedParams = { ...currentParams };

      let fieldsToProcess = [];

      if (templateVersion === 'v2') {
        // For v2 templates, process the same way as getVendorFeedSpecs
        if (vendors && data?.vendor_id && vendorEndpoints && vendorEndpointParams) {
          const vendor = vendors[data.vendor_id];
          if (vendor) {
            // Get the feeds endpoint (the one with endpoint_type === 'feeds')
            const feedsEndpoint = vendor.vendor_endpoints
              ?.map((id) => vendorEndpoints[id])
              ?.find((ep) => ep?.source_template?.endpoint_type === 'feeds');

            if (feedsEndpoint) {
              // Process the V2 template using the same logic as getVendorFeedSpecs
              const availableFeeds = Object.keys(
                data?.template_config?.feeds_config?.feeds || {},
              ).map((feedName) => ({
                feed_name: feedName,
                name: feedName,
                feed_display_name:
                  data.template_config.feeds_config.feeds[feedName]?.ui?.feed_display_name ||
                  feedName,
              }));

              const discoveryResults = {
                availableFeeds: availableFeeds,
              };

              // Get the resource parameters for this endpoint
              const resourceParams =
                feedsEndpoint.resource_parameters
                  ?.map((paramId) => vendorEndpointParams[paramId])
                  ?.filter((param) => param) || [];

              // Create a copy of the endpoint with resolved resource_parameters to avoid mutating the original
              const feedsEndpointCopy = {
                ...feedsEndpoint,
                resource_parameters: resourceParams,
              };

              const processedEndpoints = {
                feeds: [feedsEndpointCopy],
              };

              const { vendorFeedSpecs } = processV2Template(
                vendor,
                availableFeeds,
                discoveryResults,
                processedEndpoints,
                resourceParams,
              );

              // Extract params from each enabled capability (only user-configurable ones)
              Object.entries(vendorFeedSpecs?.capabilities || {}).forEach(
                ([capabilityKey, capability]) => {
                  if (
                    capability.enabled &&
                    capability.params &&
                    CapabilityChecker.isUserConfigurable(capabilityKey)
                  ) {
                    fieldsToProcess.push(...capability.params);
                  }
                },
              );

              // Also include additional_params (non-capability parameters)
              const additionalParams = vendorFeedSpecs?.additional_params || [];
              fieldsToProcess.push(...additionalParams);
            }
          }
        }
      } else {
        // For v1 templates, use traditional configFields
        if (configFields && Array.isArray(configFields)) {
          fieldsToProcess = configFields.filter(({ name, is_common_param = true }) => {
            return name !== 'vendor_endpoint_id' && is_common_param;
          });
        }
      }

      // Filter fields using shouldRenderField (same logic as edit form)
      const visibleFields = fieldsToProcess.filter((field) => {
        // Use the same conditional display logic as ResourceParamFields for ELT flows
        const shouldRender = shouldRenderFieldForELT(field, processedParams);
        const shouldHide = UIUtils.shouldHideField
          ? UIUtils.shouldHideField(field, processedParams)
          : false;

        return shouldRender && !shouldHide;
      });

      // Process only visible fields
      visibleFields.forEach((field) => {
        // Get the current value, fallback to default
        const currentValue =
          processedParams[field.name] !== undefined ? processedParams[field.name] : field.default;

        // Only add if there's a value to display
        if (currentValue !== undefined && currentValue !== '') {
          // Try to find a label from values/options array, fallback to currentValue
          let displayValue = currentValue;

          if (field.values && Array.isArray(field.values)) {
            const valueOption = field.values.find(
              (option) => option.value === currentValue || option.value === String(currentValue),
            );
            if (valueOption && valueOption.label) {
              displayValue = valueOption.label;
            }
          } else if (field.options && Array.isArray(field.options)) {
            const valueOption = field.options.find(
              (option) => option.value === currentValue || option.value === String(currentValue),
            );
            if (valueOption && valueOption.label) {
              displayValue = valueOption.label;
            }
          }

          userConfigFields.push({
            name: field.name,
            display_name: field.display_name || field.name,
            display_value: displayValue,
            value: currentValue,
            type: field.type || 'string',
            description: field.description,
          });
        }
      });

      return (
        <>
          {/* User Configuration Fields */}
          {userConfigFields.length > 0 && (
            <div className="details-accordion-group" aria-label="user-config-fields">
              {userConfigFields.map((field, idx) => (
                <div key={`user-config-${idx}`} className="detail-row">
                  <div className="detail-col">
                    <div
                      className="detail-key"
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}
                    >
                      {field.display_name}
                      {!isEmpty(field.description) && (
                        <Tooltip
                          interactive="true"
                          placement="right"
                          rootStyle={{}}
                          arrow={false}
                          tooltipTheme="fieldInfo"
                          PopperProps={DefaultTooltipPopperProps}
                          title={<MarkdownContent>{field.description}</MarkdownContent>}
                          children={
                            <div style={{ marginTop: 'var(--spacing-sm)' }}>
                              <InfoIcon style={{ width: 12, height: 12, color: '#666' }} />
                            </div>
                          }
                        />
                      )}
                    </div>
                    <div className="detail-value">{field.display_value}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Feeds Accordion */}
          <FeedsListWithSearch
            feedsList={feedsList}
            configFields={configFields}
            isExpanded={isExpanded}
            onAccordionToggle={handleAccordionToggle}
            templateVersion={templateVersion}
            data={data}
            vendors={vendors}
            sourceConfigById={sourceConfigById}
            vendorEndpoints={vendorEndpoints}
            vendorEndpointParams={vendorEndpointParams}
          />
        </>
      );
    } else {
      return null;
    }
  }, [
    flowType,
    connectionCategory,
    entityType,
    data,
    isExpanded,
    handleAccordionToggle,
    isLoadingResource,
    vendors,
    sourceConfigById,
    vendorEndpoints,
    vendorEndpointParams,
  ]);
};

const ConfigurationFields = ({
  connectionCategory,
  config,
  configFields,
  entityType,
  overrideFields,
  data,
  isLoadingResource,
  vendors,
  vendorEndpoints,
  vendorEndpointParams,
  paging,
}) => {
  // Extract sourceConfigById from paging
  const sourceConfigById = paging?.sourceConfigById;
  const fieldsToIgnore = ['mapping', 'soap.params'];
  const fields = overrideFields ? overrideFields : configFields;
  const flowType = get(data, 'flow_type', flowTypes.streaming);
  const sourceType = get(data, 'source_type', null);
  const sinkType = get(data, 'sink_type', null);

  // Get setAction from ResourceEditProvider for Edit link
  const { setAction } = useResourceEditProvider();
  const hasEditProvider = !!useContext(ResourceEditContext);

  // Use custom hooks for complex logic
  const nonEmptyFields = useFieldFiltering(
    fields,
    entityType,
    connectionCategory,
    config,
    fieldsToIgnore,
  );
  const { isExpanded, handleAccordionToggle } = useAccordionState();
  const mappingContent = useMappingContent(
    config,
    entityType,
    connectionCategory,
    flowType,
    isExpanded,
    handleAccordionToggle,
  );
  const eltConfigContent = useELTConfigContent(
    flowType,
    connectionCategory,
    entityType,
    data,
    config,
    isExpanded,
    handleAccordionToggle,
    configFields,
    isLoadingResource,
    vendors,
    sourceConfigById,
    vendorEndpoints,
    vendorEndpointParams,
  );

  const isCDC = flowType === flowTypes?.cdc;

  // Helper function for field display names
  const getFieldDisplayName = useCallback((field) => {
    if (field?.type === 'frequency') {
      return 'Poll frequency';
    }
    if (field?.name === 'cdc.mode') {
      return field?.display_name || 'Replication Method';
    }
    return field?.display_name || field?.name || '';
  }, []);

  // Helper function to render field label with Edit link for SQL queries
  const renderFieldLabel = useCallback(
    (field) => {
      const displayName = getFieldDisplayName(field);
      const isSqlQuery = isSqlQueryField(field);

      if (isSqlQuery && data?.id) {
        const handleEdit = () => {
          setAction({
            resource: entityType, // Use entityType instead of hardcoding RESOURCE_TYPE_SOURCE
            type: 'edit',
            id: data.id,
          });
        };

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingBottom: 'var(--spacing-sm)',
            }}
          >
            <span>{displayName}</span>
            {hasEditProvider && data?.id && (
              <Button
                onClick={handleEdit}
                variant="outlined"
                style={{
                  padding: '0px',
                  fontSize: '11px',
                  lineHeight: '18px',
                }}
              >
                Edit
              </Button>
            )}
          </div>
        );
      }

      return displayName;
    },
    [getFieldDisplayName, data?.id, setAction, entityType, hasEditProvider],
  );

  // Separate SQL fields from regular fields - SQL always renders last
  const sqlFields = [];
  const regularFields = [];

  nonEmptyFields.forEach((field) => {
    if (isCDC && field?.name === 'table') return;

    if (isSqlQueryField(field)) {
      sqlFields.push(field);
    } else {
      regularFields.push(field);
    }
  });

  // Render regular fields using simple pairing logic
  const configContent = [];
  const numRegularFields = regularFields.length;

  regularFields.forEach((field, idx) => {
    const isRowStart = idx % 2 === 0;
    const isLastItem = idx === numRegularFields - 1;

    if (isRowStart && !isLastItem) {
      return; // Skip, will be paired with next field
    }

    const lastField = regularFields[idx - 1];

    if (isRowStart && isLastItem) {
      // Last field, render single column
      configContent.push(
        <div key={`field-${idx}`} className="detail-row">
          <div className="detail-col">
            <div className="detail-key">{renderFieldLabel(field)}</div>
            <div className="detail-value">
              <ConfigFieldValue field={field} value={config[field.name]} />
            </div>
          </div>
        </div>,
      );
    } else {
      // Render pair (lastField and current field)
      configContent.push(
        <div key={`field-${idx}`} className="detail-row">
          <div className="detail-col">
            <div className="detail-key">{renderFieldLabel(lastField)}</div>
            <div className="detail-value">
              <ConfigFieldValue field={lastField} value={config[lastField.name]} />
            </div>
          </div>
          <div className="detail-col">
            <div className="detail-key">{renderFieldLabel(field)}</div>
            <div className="detail-value">
              <ConfigFieldValue field={field} value={config[field.name]} />
            </div>
          </div>
        </div>,
      );
    }
  });

  // Render SQL fields full-width at the end
  sqlFields.forEach((field, idx) => {
    configContent.push(
      <div key={`sql-field-${idx}`} className="detail-row">
        <div className="detail-col" style={{ width: '100%' }}>
          <div className="detail-key">{renderFieldLabel(field)}</div>
          <div className="detail-value">
            <ConfigFieldValue field={field} value={config[field.name]} />
          </div>
        </div>
      </div>,
    );
  });

  if (isLoadingResource) {
    return (
      <Stack
        minHeight={400}
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        height="100%"
      >
        <CircularProgress color="info" />
      </Stack>
    );
  }

  if (flowType === flowTypes.cdc && connectionCategory === CONNECTION_CATEGORY_DATABASE) {
    const tableSelectionContent = getConfigContentForCDC(config, sourceType);
    const tablesListContent = getCDCTablesListContent(config, isExpanded, handleAccordionToggle);

    return (
      <div>
        {configContent}
        {tableSelectionContent}
        {tablesListContent}
      </div>
    );
  }

  if (
    flowType === flowTypes.elt ||
    (flowType === flowTypes.streaming &&
      connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API_MULTI)
  ) {
    return (
      <div>
        {configContent}
        {mappingContent}
        {eltConfigContent}
      </div>
    );
  }

  return (
    <div>
      {configContent}
      {mappingContent}
    </div>
  );
};

export default ConfigurationFields;
